/**
 * "Ask to watch their device" — the diagnostics invite, moved out of the old detail drawer.
 *
 * It is asked from a user screen rather than from the Diagnostics page because that page has no
 * way to name a person except by pasting a GUID, which meant coming here to copy one first. The
 * person is already in front of you here.
 */

import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { diagnosticsApi } from '@api/endpoints/diagnostics'
import { useAuthStore } from '@store/authStore'

export interface UserDiagnosticsPanelProps {
  userId: string
}

export function UserDiagnosticsPanel({ userId }: UserDiagnosticsPanelProps) {
  // Control is Admin-only: a moderator may watch and capture, but driving somebody's app acts as
  // them, and that is a narrower door than moderation.
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = !!currentUser?.roles?.includes('Admin')

  const [reason, setReason] = useState('')
  // What to ask for beyond the event feed. Both start off: the extra powers should be a decision
  // somebody made, not the default they got by pressing the same button as always.
  const [askScreen, setAskScreen] = useState(false)
  const [askControl, setAskControl] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)

  const invite = useMutation({
    mutationFn: () =>
      diagnosticsApi.invite(userId, reason.trim() || undefined, {
        allowsScreen: askScreen,
        allowsControl: askControl,
      }),
    onSuccess: () => {
      setReason('')
      // Reset the two extras but not the reason field's habits: asking for control once should not
      // quietly arm the next person's invite as well.
      setAskScreen(false)
      setAskControl(false)
      setResult({ ok: true, message: 'Asked. It appears on the Diagnostics page once they accept.' })
    },
    onError: () => setResult({ ok: false, message: 'Could not send that request.' }),
  })

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Diagnostics
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Ask to watch what their app is doing. They have to accept, they see a bar the whole time,
        and it stops itself within the hour.
      </Typography>

      {/* Asked for separately because they are separately consented to: the notice names whichever
          of these is ticked, and the person agrees to that rather than to "diagnostics". Control is
          Admin-only — it acts as the person being helped. */}
      <FormGroup sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox size="small" checked={askScreen} onChange={(e) => setAskScreen(e.target.checked)} />
          }
          label={
            <Typography variant="caption">
              Also ask to see their screen — you request a picture, they are told what it captures
            </Typography>
          }
        />
        {isAdmin && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={askControl}
                onChange={(e) => setAskControl(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                Also ask to control their app — scrolling and moving between screens, which happens
                under their account
              </Typography>
            }
          />
        )}
      </FormGroup>

      <TextField
        fullWidth
        size="small"
        label="What are you looking into?"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        helperText="Shown to them, so agreeing means agreeing to something."
        sx={{ mb: 1 }}
      />

      {result && (
        <Alert severity={result.ok ? 'success' : 'error'} sx={{ mb: 1 }} onClose={() => setResult(null)}>
          {result.message}
        </Alert>
      )}

      <Button variant="outlined" size="small" onClick={() => invite.mutate()} disabled={invite.isPending}>
        {invite.isPending ? 'Asking…' : 'Ask to watch their device'}
      </Button>
    </Box>
  )
}
